import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { UniversalLoadingState, Card, Button, NotificationCard, UniversalPage } from '@afrilink/ui';
import {
  adminFinancialsService,
  type MonthlyDatum,
} from '@/domains/financials/services/adminFinancialsService';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { formatZAR, formatZARShort } from './components';

function TwelveMonthReportPageBody() {
  const [data, setData] = useState<MonthlyDatum[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminFinancialsService.getTwelveMonthReport().then(setData).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }, []);

  if (error) return <NotificationCard tone="danger" title="Could not load 12-month report" body={error} />;
  if (!data) return <UniversalLoadingState variant="spinner" caption="Aggregating last 12 months…" />;

  const totals = data.reduce(
    (acc, m) => ({
      revenue: acc.revenue + m.revenue.total,
      expense: acc.expense + m.expense.total,
      net: acc.net + m.net,
    }),
    { revenue: 0, expense: 0, net: 0 },
  );
  const maxRev = Math.max(...data.map((m) => m.revenue.total), 1);

  function exportCsv() {
    const header = [
      'Month', 'Hosting', 'Domains', 'Other', 'Revenue',
      'WHM hosting', 'domains.co.za top-up', 'PayFast fees',
      'Internet', 'Infrastructure', 'Tooling', 'Salary',
      'Marketing', 'Other expenses', 'Total expenses', 'Net',
    ];
    const dataRows = data!.map((m) => [
      m.month_label, m.revenue.hosting, m.revenue.domains, m.revenue.other, m.revenue.total,
      m.expense.whm_hosting, m.expense.domains_topup, m.expense.payfast_fees,
      m.expense.internet, m.expense.infrastructure, m.expense.tooling,
      m.expense.salary, m.expense.marketing, m.expense.other, m.expense.total, m.net,
    ]);
    exportRowsAsCsv(`12-month-report-${todayStamp()}.csv`, header, dataRows);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">12-mo revenue</p>
          <p className="mt-1 text-2xl font-semibold text-success">{formatZAR(totals.revenue)}</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">12-mo expense</p>
          <p className="mt-1 text-2xl font-semibold text-danger">{formatZAR(totals.expense)}</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">12-mo net</p>
          <p className={`mt-1 text-2xl font-semibold ${totals.net >= 0 ? 'text-success' : 'text-danger'}`}>{formatZAR(totals.net)}</p>
        </Card>
      </div>

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Revenue trend (12 months)</h3>
          <p className="text-xs text-muted-foreground">Visual comparison of monthly revenue. Bar height ∝ total revenue.</p>
        </header>
        <div className="grid grid-cols-12 gap-2 px-4 pt-4 pb-2">
          {data.map((m) => {
            const h = (m.revenue.total / maxRev) * 100;
            return (
              <div key={m.month_start} className="flex flex-col items-center justify-end" title={`${m.month_label}: ${formatZAR(m.revenue.total)}`}>
                <div className="flex h-32 w-full items-end">
                  <div
                    className="w-full rounded-t bg-success/70 transition-all"
                    style={{ height: `${Math.max(h, 1)}%` }}
                  />
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground">{(m.month_label.split(' ')[0] ?? '').slice(0, 3)}</p>
              </div>
            );
          })}
        </div>
      </Card>

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Monthly breakdown</h3>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-card-subtle text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Month</th>
                <th className="px-4 py-2 text-right">Hosting</th>
                <th className="px-4 py-2 text-right">Domains</th>
                <th className="px-4 py-2 text-right">Other</th>
                <th className="px-4 py-2 text-right border-l border-border">Revenue</th>
                <th className="px-4 py-2 text-right">Expenses</th>
                <th className="px-4 py-2 text-right border-l border-border">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((m) => (
                <tr key={m.month_start}>
                  <td className="px-4 py-2 text-foreground">{m.month_label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatZARShort(m.revenue.hosting)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatZARShort(m.revenue.domains)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatZARShort(m.revenue.other)}</td>
                  <td className="px-4 py-2 text-right font-medium text-success tabular-nums border-l border-border">
                    {formatZARShort(m.revenue.total)}
                  </td>
                  <td className="px-4 py-2 text-right text-danger tabular-nums">{formatZARShort(m.expense.total)}</td>
                  <td className={`px-4 py-2 text-right font-semibold tabular-nums border-l border-border ${m.net >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatZARShort(m.net)}
                  </td>
                </tr>
              ))}
              <tr className="bg-card-subtle font-semibold">
                <td className="px-4 py-2">Total</td>
                <td colSpan={3} />
                <td className="px-4 py-2 text-right text-success tabular-nums border-l border-border">{formatZARShort(totals.revenue)}</td>
                <td className="px-4 py-2 text-right text-danger tabular-nums">{formatZARShort(totals.expense)}</td>
                <td className={`px-4 py-2 text-right tabular-nums border-l border-border ${totals.net >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatZARShort(totals.net)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/**
 * These five pages previously rendered NO page header at all — no title, no
 * breadcrumbs — because FinancialReportsLayout supplied a nav row and nothing
 * else. That was a §4.4 violation hiding inside a §4.1 one, and it is why no
 * financial report had a back link.
 */
export default function TwelveMonthReportPage() {
  return (
    <UniversalPage
      title="12-month report"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Financials', href: '/admin/financials' },
        { label: '12-month report' },
      ]}
    >
      <TwelveMonthReportPageBody />
    </UniversalPage>
  );
}
